import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, FileOutput } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '~/components/common/combobox/combobox';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { Switch } from '~/components/ui/switch';
import { joinPath } from '~/features/integrations/utils/remote-paths';
import {
  findSftpDeliveryTarget,
  hasPerDeliveryPlaceholder,
  renderOutputFileName,
} from '~/features/integrations/utils/sftp-connection';
import { useListConnections } from '~/features/onboarding/hooks/use-connections';
import { FileLayoutChoice } from '~/features/transformers/components/file-layout/file-layout-choice';
import { LayoutDeliveredTo } from '~/features/transformers/components/file-layout/layout-delivered-to';
import { useFileLayout } from '~/features/transformers/hooks/use-file-layout';
import { useAuthStore } from '~/store/auth.store';
import { canAccessNavPath } from '~/utils/access';
import { formatUtcNowCompact } from '~/utils/date';

import type { FeedFormat } from '../constants/enums';
import { supportsFeedFilePush } from '../constants/sftp-delivery';
import { useConnectionCapabilities } from '../hooks/use-connection-capabilities';
import { useTriggerDelivery } from '../hooks/use-trigger-delivery';
import type { TriggerDeliveryRequest } from '../types/delivery-runs.types';

/** The Export sheet's current choices — delivered exactly as they'd download. */
export interface ExportDeliverySelection {
  /** Enrichment workflow id of the run being exported. */
  sourceWorkflowId: string;
  /** Export column keys; `null` = every column. */
  fields: string[] | null;
  /** The template the sheet has selected (sent explicitly, raw included). */
  template: string | null;
  format: FeedFormat;
  /** Translated export, or `null` for English. */
  locale: string | null;
  /** The run's connector, for resolving the connection's file layout. Defaults to GMC. */
  connector?: 'GMC' | 'META';
  /** The transformer a Lily user picked in the Export sheet, preselected as "only one file". */
  pickedTransformerId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selection: ExportDeliverySelection;
  /** Run label, for the dialog copy. */
  label: string;
  /** Called after a successful trigger so the caller can close the Export sheet too. */
  onDelivered?: () => void;
}

/**
 * "Deliver to SFTP" from the Export sheet: pick an SFTP connection and send the
 * run there using the sheet's own columns, template, format and language — so
 * export and delivery share one interface and one set of choices.
 *
 * The trigger carries `use_export_config`, asking the delivery service to have
 * the export service build the file (docs/export-deliver-to-sftp.md). Gated
 * per connection on what an SFTP delivery needs: an OUTPUT target, and a
 * backend that advertises `feed_file: push`.
 */
export function ExportDeliverSftpDialog({
  open,
  onOpenChange,
  selection,
  label,
  onDelivered,
}: Props) {
  const { t } = useTranslation('deliveries');
  const { t: tl } = useTranslation('transformers');
  const navigate = useNavigate();
  // Same predicate as the `/deliveries` route guard (tenant + super admins).
  const canOpenRun = useAuthStore((st) => canAccessNavPath('/deliveries', st.user));
  const subsidiaryCode = useAuthStore((st) => {
    const id = st.user?.activeTenantId ?? '';
    const slug = st.user?.accessibleTenants.find((tenant) => tenant.id === id)?.slug;
    return slug !== undefined && slug !== '' ? slug : id;
  });

  const { data: connections = [], isLoading: connectionsLoading } = useListConnections(['ACTIVE']);
  const sftpConnections = connections.filter((c) => c.type === 'SFTP');
  const [connectionId, setConnectionId] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const selected = sftpConnections.find((c) => c.id === connectionId);
  const subsidiaryId = useAuthStore((st) => st.user?.activeTenantId ?? '');

  // Every file the connection gets (one per assigned transformer), or one a Lily user picks.
  const [pickedId, setPickedId] = useState(selection.pickedTransformerId ?? null);
  const layout = useFileLayout({
    subsidiaryId,
    connector: selection.connector ?? 'GMC',
    connectionId: selected ? connectionId : undefined,
    pickedId,
    enabled: open && !!selected,
  });

  const target = selected ? findSftpDeliveryTarget(selected.config) : null;
  const capabilities = useConnectionCapabilities(selected ? connectionId : undefined, !!selected);
  const pushSupported = supportsFeedFilePush(capabilities.data);

  const deliver = useTriggerDelivery(false);
  const dryRunMutation = useTriggerDelivery(true);
  const active = dryRun ? dryRunMutation : deliver;

  const canSubmit =
    Boolean(selected) &&
    Boolean(target) &&
    !capabilities.isLoading &&
    pushSupported &&
    // Wait for resolve, and block on a non-403 failure rather than sending the wrong file.
    !layout.isLoading &&
    !layout.failed &&
    !layout.pickPending &&
    layout.clashes.length === 0;

  const remotePath = target
    ? joinPath(
        target.path || '/',
        renderOutputFileName(target.fileName, {
          subsidiary_code: subsidiaryCode,
          date: formatUtcNowCompact('date'),
          timestamp: formatUtcNowCompact('timestamp'),
        }),
      )
    : null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Only a Lily user's pick is sent (customers get 403); integrations refuses
    // `feed_format` / `field_filter` / `export_template` beside `transformer_id`.
    const transformerId = layout.canChoose ? pickedId : null;
    const body: TriggerDeliveryRequest = {
      workflowId: selection.sourceWorkflowId,
      deliveryMethod: 'SFTP',
      useExportConfig: true,
      // A transformer owns format, columns and encoding; only the standard path sends them.
      ...(!layout.applies ? { feedFormat: selection.format } : {}),
      ...(!layout.applies && selection.fields ? { fieldFilter: selection.fields } : {}),
      ...(!layout.applies && selection.template ? { exportTemplate: selection.template } : {}),
      ...(selection.locale ? { exportLocale: selection.locale } : {}),
      ...(transformerId ? { transformerId } : {}),
    };
    active.mutate(
      { connectionId, body },
      {
        onSuccess: (job) => {
          onOpenChange(false);
          onDelivered?.();
          // The hook already toasts; only users the console admits can open the run page.
          if (!canOpenRun) return;
          void navigate({
            to: '/deliveries/$runId',
            params: { runId: job.id },
            search: { tab: 'overview' },
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="delivery-export-sftp-dialog">
        <DialogHeader>
          <DialogTitle>{t('exportDeliver.title')}</DialogTitle>
          <DialogDescription>{t('exportDeliver.description', { name: label })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="export-deliver-connection" className="text-sm">
              {t('exportDeliver.connection')}
            </Label>
            <Combobox
              id="export-deliver-connection"
              options={sftpConnections.map((c) => ({ value: c.id, label: c.name }))}
              value={connectionId}
              onChange={setConnectionId}
              placeholder={t('exportDeliver.connectionPlaceholder')}
              searchPlaceholder={t('trigger.searchConnections')}
              className="w-full"
              testId="delivery-export-sftp-connection-select"
            />
            {!connectionsLoading && sftpConnections.length === 0 && (
              <p className="text-muted-foreground text-xs">{t('exportDeliver.noConnections')}</p>
            )}
          </div>

          {selected && !target && (
            <Notice>{t('trigger.sftp.noTarget', { name: selected.name })}</Notice>
          )}
          {selected && target && !capabilities.isLoading && !pushSupported && (
            <Notice>{t('trigger.sftp.pushUnsupported')}</Notice>
          )}

          {selected && target && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{tl('layout.title')}</legend>
              <FileLayoutChoice
                layout={layout}
                mode="delivery"
                pickedId={pickedId}
                onPick={setPickedId}
                idPrefix="export-deliver-layout"
              />
            </fieldset>
          )}

          {/* A transformer names its own file(s): show those, not the standard name. */}
          {target && layout.applies && (
            <LayoutDeliveredTo
              layout={layout}
              directory={target.path}
              label={t('trigger.sftp.target')}
            />
          )}
          {remotePath && target && !layout.applies && (
            <div className="space-y-1.5">
              <Label className="text-sm">{t('trigger.sftp.target')}</Label>
              <p className="bg-muted/40 flex items-center gap-2 rounded-md border px-3 py-2 font-mono text-xs break-all">
                <FileOutput aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
                {remotePath}
              </p>
              {hasPerDeliveryPlaceholder(target.fileName) && (
                <p className="text-muted-foreground text-xs">{t('trigger.sftp.approximateName')}</p>
              )}
            </div>
          )}

          {!layout.applies && (
            <p className="text-muted-foreground text-xs">
              {t('exportDeliver.summary', {
                format: selection.format,
                columns: selection.fields
                  ? t('exportDeliver.someColumns', { count: selection.fields.length })
                  : t('exportDeliver.allColumns'),
              })}
            </p>
          )}

          <div className="flex items-center justify-between border-t pt-4">
            <Label htmlFor="export-deliver-dry" className="text-sm">
              {t('trigger.dryRunToggle')}
            </Label>
            <Switch
              id="export-deliver-dry"
              data-testid="delivery-export-sftp-dry-run-toggle"
              className="cursor-pointer"
              checked={dryRun}
              onCheckedChange={setDryRun}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="cursor-pointer"
            data-testid="delivery-export-sftp-cancel-button"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('trigger.cancel')}
          </Button>
          <Button
            className="cursor-pointer"
            onClick={handleSubmit}
            data-testid="delivery-export-sftp-submit-button"
            isLoading={active.isPending}
            disabled={!canSubmit}
          >
            {dryRun ? t('trigger.submitDryRun') : t('exportDeliver.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Inline blocker banner. */
function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p
      data-testid="delivery-export-sftp-notice"
      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/20 dark:text-amber-300"
    >
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
